import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import noop from 'lodash/noop';
import SupportedLanguages from '../setup/intl/enums';
import useUserContext from '../hooks/useUserContext';
import { User } from './User/types';
import { getLocalStorageValue } from '../hooks/useLocalStorageValue/utils';
import { LOCAL_STORAGE_LANGUAGE_KEY } from './consts';
import { getEnumValueByStringValue } from '../utils/misc';

export interface LanguageContextValue {
  language: SupportedLanguages;
  setLanguage: (language: SupportedLanguages) => void;
}

export const LanguageContext = React.createContext<LanguageContextValue>({
  language: SupportedLanguages.English,
  setLanguage: noop,
});

export const { Provider } = LanguageContext;

const getLanguage = (user: User | undefined) => {
  const storedLang = getEnumValueByStringValue(
    SupportedLanguages,
    getLocalStorageValue(LOCAL_STORAGE_LANGUAGE_KEY) ?? ''
  );

  if (storedLang) {
    return storedLang;
  }

  if (user) {
    const userLanguage = user.accountDetails.language;
    localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, userLanguage);
    return userLanguage;
  }

  const locale = navigator.language.split(/[-_]/)[0];
  return locale === SupportedLanguages.English || locale === SupportedLanguages.Arabic
    ? locale
    : SupportedLanguages.English;
};

export const LanguageContextProvider: React.FC = ({ children }) => {
  const { user } = useUserContext();
  const [language, setLang] = useState(getLanguage(user));
  const userOverrideRef = useRef(false);

  const setLanguage = useCallback((lang: SupportedLanguages) => {
    localStorage.setItem(LOCAL_STORAGE_LANGUAGE_KEY, lang);
    userOverrideRef.current = true;
    setLang(lang);
  }, []);

  const LanguageContextValue = useMemo(
    () => ({
      language,
      setLanguage,
    }),
    [language, setLanguage]
  );

  const userAccountLanguage = user?.accountDetails?.language;
  useEffect(() => {
    // If user explicitly changed the language via the UI, don't override it
    // until the saved account language actually changes on the server
    if (userOverrideRef.current) {
      // Clear override once server language matches what user selected
      if (userAccountLanguage === language) {
        userOverrideRef.current = false;
      }
      return;
    }
    const newLanguage = getLanguage(user);
    if (newLanguage !== language) {
      setLang(newLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAccountLanguage]);

  return <Provider value={LanguageContextValue}>{children}</Provider>;
};
